<template>
  <div
    :class="bem()"
  >
    <div :class="bem('inner')">
      <div :class="bem('image-wrapper')">
        <DatoCmsImage
          v-if="heroImage?.responsiveImage"
          :data="heroImage.responsiveImage"
          layout="responsive"
          :class="bem('image')"
          sizes="(max-width: 1280px) 100vw, 40vw"
        />
      </div>
      <div :class="bem('content')">
        <VisionHeading
          tag="h2"
          size="h2"
          :class="bem('title')"
        >
          Specification
        </VisionHeading>
        <VisionAccordion
          v-if="props.customisedProduct.lensSpec"
          :default-expanded="true"
          :no-border="true"
          :class="bem('accordion')"
        >
          <template #header>
            <VisionHeading
              tag="h4"
              size="h4"
            >
              Lenses
            </VisionHeading>
          </template>
          <template #body>
            <div :class="bem('group')">
              <span :class="bem('divider')" />
              <template
                v-for="spec in computedLensSpec"
                :key="spec"
              >
                <ProductSpecificationItem
                  v-if="spec?.title && spec?.value"
                  :title="spec.title"
                  :value="spec.value"
                  :tooltip="spec.tooltip"
                />
                <span
                  v-if="spec?.title && spec?.value"
                  :class="bem('divider')"
                />
              </template>
            </div>
          </template>
        </VisionAccordion>
        <VisionAccordion
          v-if="props.customisedProduct.frameSpec?.material"
          :no-padding="true"
          :no-border="true"
          :class="bem('accordion')"
        >
          <template #header>
            <VisionHeading
              tag="h4"
              size="h4"
              :class="bem('accordion-title')"
            >
              Frames
            </VisionHeading>
          </template>
          <template #body>
            <div :class="bem('group')">
              <span :class="bem('divider')" />
              <template
                v-for="spec in props.customisedProduct.frameSpec"
                :key="spec"
              >
                <ProductSpecificationItem
                  v-if="spec?.title && spec?.value"
                  :title="spec.title"
                  :value="spec.value"
                  :tooltip="spec.tooltip"
                />
                <span
                  v-if="spec?.title && spec?.value"
                  :class="bem('divider')"
                />
              </template>
            </div>
          </template>
        </VisionAccordion>
        <VisionAccordion
          v-if="props.customisedProduct.measurements"
          :no-padding="true"
          :no-border="true"
          :class="bem('accordion')"
        >
          <template #header>
            <VisionHeading
              tag="h4"
              size="h4"
              :class="bem('accordion-title')"
            >
              Measurements
            </VisionHeading>
          </template>
          <template #body>
            <ProductSpecificationMeasurements
              :customised-product="props.customisedProduct"
            />
          </template>
        </VisionAccordion>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Image as DatoCmsImage } from 'vue-datocms'
import CustomisedProduct from '~/models/CustomisedProduct'

const bem = useBEM('ProductSpecification')

const props = defineProps({
  customisedProduct: { type: CustomisedProduct, required: true },
})

const computedLensSpec = computed(() => {
  if (!props.customisedProduct.lensSpec) return null
  return {
    material: props.customisedProduct.lensSpec?.material,
    vlt: {
      title: 'VLT',
      tooltip:
        'VLT stands for Visible Light Transmission. It measures how much light passes through a lens to reach the eye. The lower the VLT, the darker the tint of your lenses.',
      value: computedLensDetails.value.vlt,
    },
    abbe: {
      title: 'ABBE Value',
      tooltip:
        'Abbe Value is a measure of the optical clarity of a lens material. The higher the Abbe Value, the better the optical clarity of your lens.',
      value: props.customisedProduct.lensSpec?.abbe,
    },
    polarised: {
      title: 'Polarised',
      tooltip:
        'Polarised lenses contain an invisible microfilter which cuts out glare. Colours appear brighter and bolder, and your eyes feel more relaxed.',
      value: props.customisedProduct.lensTech?.includes('kop') ? 'Yes' : 'No',
    },
    category: {
      title: 'Filter Category',
      tooltip: 'Filter Category defines how much light is blocked by the lens, from Category 0 (clear) to Category 4 (very dark for intense sun).',
      value: computedLensDetails.value.category,
    },
    uv: {
      title: 'UV Protection',
      tooltip:
        'Your SunGods block 100% of UVA and UVB radiation, meeting UV400 safety standards. This means they protect your eyes from harmful ultraviolent (UV) solar radiation.',
      value: '100%',
    },
    photochromic: {
      title: 'Photochromic',
      tooltip: 'Photochromic lenses respond to light conditions, darkening in bright sun and lightening in low light to ensure uninterrupted vision.',
      value: computedLensDetails.value.photochromic ? 'Yes' : 'No',
    },
    changeable: {
      title: 'Changeable Lenses',
      value: props.customisedProduct.lensSpec?.changeable
        ? `Yes (${props.customisedProduct.lensSpec?.changeable} Seconds)`
        : null,
    },
    shape: {
      title: 'Shape',
      value: props.customisedProduct.lensSpec?.shape
    },
  }
})

const computedLensDetails = computed(() => {
  if (props.customisedProduct.lensSpec?.vlt?.includes('-')) {
    const multipleVlts = props.customisedProduct.lensSpec.vlt.split('-')
    return {
      vlt: multipleVlts[0] + ' to ' + multipleVlts[1],
      category: props.customisedProduct.lensSpec?.vlt
        ? `Cat.${getLensCategoryFromVlt(
            multipleVlts[0]
          )} to Cat.${getLensCategoryFromVlt(multipleVlts[1])}`
        : null,
      photochromic: true,
    }
  }
  return {
    vlt: props.customisedProduct.lensSpec?.vlt || null,
    category: props.customisedProduct.lensSpec?.vlt
      ? `Cat.${getLensCategoryFromVlt(props.customisedProduct.lensSpec.vlt)}`
      : null,
    photochromic: false,
  }
})

function getLensCategoryFromVlt(vlt: number) {
  if (!vlt) return null
  if (vlt <= 8) return 4
  if (vlt <= 18 && vlt > 8) return 3
  if (vlt <= 43 && vlt > 18) return 2
  if (vlt <= 80 && vlt > 43) return 1
  else return 0
}

const heroImage = computed(() => {
  return (
    props.customisedProduct.product.pdpConfig?.technicalImageHero ||
    props.customisedProduct.product.baseProduct?.pdpConfig?.technicalImageHero
  )
})
</script>

<style lang="postcss">
.ProductSpecification {
  --vision-icon-fill: theme('colors.white');
  @apply text-white bg-black py-[var(--outer-gutter)];
}

.ProductSpecification__inner {
  @apply relative global-wrapper;

  @screen md {
    @apply grid gap-12 items-start;
    grid-template-columns: 2fr minmax(360px, 1fr);
  }
}

.ProductSpecification__title {
  @apply mb-6 md:mb-8;
}

.ProductSpecification__accordion {
  @apply border-t-2 border-grey-500;
}

.ProductSpecification__group {
  @apply grid gap-3 md:gap-4 pb-6 md:pb-8;
}

.ProductSpecification__divider {
  @apply h-px w-full bg-grey-800;
}

.ProductSpecification__image-wrapper {
  @apply block my-8 md:my-0;
}
</style>
